import { useEffect, useRef } from 'react';
import { UilCheck, UilSetting } from '@iconscout/react-unicons';
import clsx from 'clsx';
import { VideoQuality } from '../../lib/types';

interface VideoQualityMenuProps {
    qualities: VideoQuality[];
    selectedQuality: VideoQuality;
    onSelect: (quality: VideoQuality) => void;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    className?: string;
    buttonClassName?: string;
}

export function VideoQualityMenu({
    qualities,
    selectedQuality,
    onSelect,
    isOpen,
    onOpenChange,
    className,
    buttonClassName,
}: VideoQualityMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                onOpenChange(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onOpenChange]);

    if (qualities.length <= 1) return null;

    return (
        <div className={clsx('video-quality-menu', className)} ref={menuRef}>
            <button
                ref={buttonRef}
                type="button"
                className={clsx('video-quality-menu-button', buttonClassName)}
                aria-label={`Kvalita videa (${selectedQuality.label})`}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                onClick={(event) => {
                    event.stopPropagation();
                    onOpenChange(!isOpen);
                }}
            >
                <UilSetting />
                <span className="sr-only">{selectedQuality.label}</span>
            </button>

            {isOpen ? (
                <div className="video-quality-menu-dropdown">
                    <p className="video-quality-menu-dropdown-label">
                        Kvalita
                    </p>
                    <div
                        role="listbox"
                        aria-label="Kvalita videa"
                        className="video-quality-menu-dropdown-list"
                    >
                        {qualities.map((quality) => {
                            const isSelected =
                                quality.height === selectedQuality.height;

                            return (
                                <button
                                    key={quality.height}
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    className={clsx(
                                        'video-quality-menu-option',
                                        isSelected &&
                                            'video-quality-menu-option--selected'
                                    )}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onSelect(quality);
                                        onOpenChange(false);
                                    }}
                                >
                                    <span>{quality.label}</span>
                                    {isSelected ? (
                                        <UilCheck size={14} />
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
